/**
 * Rozliczenie prosumenta: ile wart jest prąd z paneli według taryfy klienta i net-billingu.
 * Czysta arytmetyka, bez Reacta. Używa jej `oplacalnosc.js` (zwrot, 25 lat) i teczka (zł dziennie).
 *
 * ═══ DWIE CENY, DWIE ZASADY ═══
 * 1. Prąd z paneli zużyty u siebie oszczędza CAŁĄ zmienną cenę zakupu: energię i dystrybucję z opłatami
 *    za kWh. W G12 i G12w zależy, o której godzinie go zużyjesz, więc cena jest średnią stref ważoną
 *    udziałem tańszych godzin w autokonsumpcji (`UDZIAL_PV_W_TANIEJ` w `config/taryfy.js`).
 * 2. Nadwyżka trafia do depozytu po cenie rynkowej razy 1,23, ale depozyt płaci WYŁĄCZNIE za energię
 *    kupioną z sieci, nie za dystrybucję. Czego nie wykorzystasz w 12 miesięcy, sprzedawca zwraca,
 *    najwyżej 30% (RCE) albo 20% (RCEm) wartości energii oddanej; reszta przepada. Dlatego za duża
 *    instalacja traci na każdej kolejnej kilowatogodzinie, a nie „sprzedaje do sieci”.
 *
 * Bilans liczymy rocznie, bo depozyt żyje 12 miesięcy: letnia nadwyżka płaci za zimowy prąd z sieci.
 * Wartość depozytu rozdzielamy potem na miesiące proporcjonalnie do oddanej energii, żeby „lipiec:
 * ok. 9 zł dziennie” sumował się do kwoty rocznej.
 */

import {
  TARYFY, TARYFA_DOMYSLNA, TARYFA_NIE_WIEM, taryfaPoKluczu, UDZIAL_PV_W_TANIEJ, UDZIAL_ZUZYCIA_W_TANIEJ,
} from '../config/taryfy'
import {
  CENA_ZAKUPU_ZL_KWH, CENA_SPRZEDAZY_ZL_KWH, CENA_SPRZEDAZY_OD_ZL_KWH, CENA_SPRZEDAZY_DO_ZL_KWH,
  SYSTEMY_ROZLICZEN, SYSTEM_ROZLICZEN_DOMYSLNY,
} from '../config/cenyEnergii'
import { jestPolska } from '../kraj'

export const KLUCZE_TARYF = [...TARYFY.map((t) => t.key), TARYFA_NIE_WIEM]

/** Klucz taryfy z dowolnego zapisu („g12w”, „G12W”); nieznana albo pusta to `null`. */
export const kluczTaryfy = (v) => {
  if (typeof v !== 'string') return null
  const s = v.trim().toLowerCase()
  if (s === TARYFA_NIE_WIEM || s === 'nie wiem') return TARYFA_NIE_WIEM
  return TARYFY.find((t) => t.key.toLowerCase() === s)?.key || null
}

const dodatnia = (v) => (Number.isFinite(v) && v > 0 ? v : null)
const suma = (t) => t.reduce((a, b) => a + b, 0)
const srednia = (t) => suma(t) / t.length

/** Cena strefy: domyślna albo klienta. Część energii klienta to jego cena razy proporcja domyślna. */
const strefaCeny = (domyslna, cenaKlienta) => {
  const c = dodatnia(cenaKlienta)
  if (!c) return { ...domyslna, zrodlo: 'domyslna' }
  return {
    ...domyslna,
    cenaZlKwh: c,
    energiaZlKwh: c * (domyslna.energiaZlKwh / domyslna.cenaZlKwh),
    zrodlo: 'klient',
  }
}

/**
 * Ceny klienta gotowe do rachunku.
 *
 * @param {object} p
 * @param {string|null} [p.taryfa] 'G11' | 'G12' | 'G12w' | 'nie_wiem'; „nie wiem” i brak liczymy jak G11
 * @param {number|null} [p.cenaZakupu] cena z rachunku klienta, zł/kWh brutto z dystrybucją: w G11 jedyna,
 *   w G12 i G12w strefy droższej
 * @param {number|null} [p.cenaZakupuTania] w G12 i G12w cena strefy tańszej
 * @param {number|null} [p.cenaSprzedazy] wartość kWh oddanego w depozycie (z 1,23), gdy klient ją zna
 * @param {string|null} [p.systemRozliczen] 'rce' | 'rcem'
 * @returns {{
 *   taryfa: string, wybrana: string|null, nazwa: string,
 *   strefy: Array<{key:string, label:string, cenaZlKwh:number, energiaZlKwh:number, zrodlo:string}>,
 *   udzialPvTania: number[], udzialZuzyciaTania: number,
 *   cenaPvZlKwh: number[], cenaZakupuZlKwh: number, energiaZakupuZlKwh: number,
 *   wartoscOddanejZlKwh: number, wartoscOddanejOdDo: {od:number, do:number},
 *   system: string, procentZwrotu: number,
 *   zrodlaCen: {zakup: 'domyslne'|'klient'|'czesciowo', sprzedaz: 'zalozenie'|'klient'},
 * }}
 *   `cenaPvZlKwh` 12 cen kWh z paneli zużytej u siebie (miesiące różnią się udziałem tańszych godzin),
 *   `cenaZakupuZlKwh` średnia cena kWh zużycia domu (koszt prądu bez paneli), `energiaZakupuZlKwh` część
 *   energii w kWh kupionym z sieci (tyle najwyżej pokrywa depozyt)
 */
export const rozliczenieKlienta = ({
  taryfa = null, cenaZakupu = null, cenaZakupuTania = null, cenaSprzedazy = null, systemRozliczen = null,
} = {}) => {
  const wybrana = kluczTaryfy(taryfa)
  const sys = SYSTEMY_ROZLICZEN.find((s) => s.key === systemRozliczen)
    || SYSTEMY_ROZLICZEN.find((s) => s.key === SYSTEM_ROZLICZEN_DOMYSLNY)
  const sprzedazKlienta = Number.isFinite(cenaSprzedazy) && cenaSprzedazy >= 0 ? cenaSprzedazy : null
  const sprzedaz = {
    wartoscOddanejZlKwh: sprzedazKlienta ?? CENA_SPRZEDAZY_ZL_KWH,
    wartoscOddanejOdDo: sprzedazKlienta != null
      ? { od: sprzedazKlienta, do: sprzedazKlienta }
      : { od: CENA_SPRZEDAZY_OD_ZL_KWH, do: CENA_SPRZEDAZY_DO_ZL_KWH },
    system: sys.key,
    procentZwrotu: sys.procentZwrotu,
  }

  // Poza Polską nie mamy taryf: jedna cena kraju, a depozyt bez limitu energii (taki rachunek jak przed
  // taryfami; pieniędzy poza Polską i tak nie pokazujemy, decyzja właściciela 2026-10-08).
  if (!jestPolska) {
    const cena = dodatnia(cenaZakupu) || CENA_ZAKUPU_ZL_KWH
    return {
      taryfa: TARYFA_DOMYSLNA,
      wybrana,
      nazwa: '',
      strefy: [{ key: 'calodobowa', label: 'cała doba', cenaZlKwh: cena, energiaZlKwh: cena, zrodlo: dodatnia(cenaZakupu) ? 'klient' : 'domyslna' }],
      udzialPvTania: Array(12).fill(0),
      udzialZuzyciaTania: 0,
      cenaPvZlKwh: Array(12).fill(cena),
      cenaZakupuZlKwh: cena,
      energiaZakupuZlKwh: cena,
      ...sprzedaz,
      zrodlaCen: { zakup: dodatnia(cenaZakupu) ? 'klient' : 'domyslne', sprzedaz: sprzedazKlienta != null ? 'klient' : 'zalozenie' },
    }
  }

  const t = taryfaPoKluczu(wybrana && wybrana !== TARYFA_NIE_WIEM ? wybrana : TARYFA_DOMYSLNA)
  const [domDroga, domTania] = t.strefy
  const droga = strefaCeny(domDroga, cenaZakupu)
  const tania = domTania ? strefaCeny(domTania, cenaZakupuTania) : null
  const strefy = tania ? [droga, tania] : [droga]

  const udzialPvTania = tania ? UDZIAL_PV_W_TANIEJ[t.key] : Array(12).fill(0)
  const uz = tania ? UDZIAL_ZUZYCIA_W_TANIEJ[t.key] : 0
  const miesz = (a, b, u) => a * (1 - u) + b * u
  const cenaPvZlKwh = udzialPvTania.map((u) => (tania ? miesz(droga.cenaZlKwh, tania.cenaZlKwh, u) : droga.cenaZlKwh))
  const zKlienta = strefy.filter((s) => s.zrodlo === 'klient').length

  return {
    taryfa: t.key,
    wybrana,
    nazwa: t.label,
    strefy,
    udzialPvTania,
    udzialZuzyciaTania: uz,
    cenaPvZlKwh,
    cenaZakupuZlKwh: tania ? miesz(droga.cenaZlKwh, tania.cenaZlKwh, uz) : droga.cenaZlKwh,
    energiaZakupuZlKwh: tania ? miesz(droga.energiaZlKwh, tania.energiaZlKwh, uz) : droga.energiaZlKwh,
    ...sprzedaz,
    zrodlaCen: {
      zakup: zKlienta === 0 ? 'domyslne' : (zKlienta === strefy.length ? 'klient' : 'czesciowo'),
      sprzedaz: sprzedazKlienta != null ? 'klient' : 'zalozenie',
    },
  }
}

/**
 * Rozliczenie z dawnych `ceny = { zakup, sprzedaz }` (jedna cena zakupu): taryfa G11 z ceną klienta.
 * Dla wołających sprzed taryf; `null` albo pusty obiekt to ceny domyślne G11.
 */
export const rozliczenieZCen = (ceny = {}) => rozliczenieKlienta({
  taryfa: TARYFA_DOMYSLNA,
  cenaZakupu: ceny?.zakup ?? null,
  cenaSprzedazy: ceny?.sprzedaz ?? null,
})

/**
 * Wartość energii w jednym roku eksploatacji.
 *
 * @param {{miesiace?:Array, rocznie:object}} p wynik `prognoza` (z miesiącami, gdy są)
 * @param {object} r wynik `rozliczenieKlienta`
 * @param {{zakup?:number, sprzedaz?:number}} [mnozniki] wzrost cen od pierwszego roku (1 = bez zmian)
 * @returns {null|{
 *   miesiace: Array<{zOszczednosciZl, depozytZl, razemZl, zlDziennie}>|null,
 *   zOszczednosciZl, depozytZl, wykorzystanyZl, zwrotNadplatyZl, przepadaZl, zeSprzedazyZl, razemZl,
 *   kosztBezPvZl, cenaZakupuZlKwh, wartoscOddanejZlKwh,
 * }}
 *   `depozytZl` wartość energii oddanej (z 1,23), `zeSprzedazyZl` jej część, która realnie wróciła: zapłacona
 *   energia z sieci plus zwrot nadpłaty. `null` bez znanego zużycia (nie da się oddzielić autokonsumpcji).
 */
export const wartoscEnergii = (p, r, { zakup = 1, sprzedaz = 1 } = {}) => {
  const ro = p?.rocznie
  if (!ro || ro.autokonsumpcjaKwh == null) return null
  const wartoscOddanej = r.wartoscOddanejZlKwh * sprzedaz
  const maMiesiace = Array.isArray(p.miesiace) && p.miesiace.length === 12

  const miesiace = maMiesiace
    ? p.miesiace.map((m, i) => ({
      zOszczednosciZl: (m.autokonsumpcjaKwh || 0) * r.cenaPvZlKwh[i] * zakup,
      depozytZl: (m.nadwyzkaKwh || 0) * wartoscOddanej,
      dni: m.dni,
    }))
    : null
  const zOszczednosci = miesiace
    ? suma(miesiace.map((m) => m.zOszczednosciZl))
    : ro.autokonsumpcjaKwh * srednia(r.cenaPvZlKwh) * zakup
  const depozyt = miesiace ? suma(miesiace.map((m) => m.depozytZl)) : ro.nadwyzkaKwh * wartoscOddanej

  // Depozyt płaci tylko za energię z sieci; nadwyżka nad tym wraca najwyżej w `procentZwrotu`.
  const limit = (ro.zSieciKwh || 0) * r.energiaZakupuZlKwh * zakup
  const wykorzystany = Math.min(depozyt, limit)
  const zwrot = Math.min(depozyt - wykorzystany, r.procentZwrotu * depozyt)
  const zeSprzedazy = wykorzystany + zwrot
  const udzial = depozyt > 0 ? zeSprzedazy / depozyt : 0

  return {
    miesiace: miesiace && miesiace.map((m) => {
      const razem = m.zOszczednosciZl + m.depozytZl * udzial
      return {
        zOszczednosciZl: m.zOszczednosciZl,
        depozytZl: m.depozytZl * udzial,
        razemZl: razem,
        zlDziennie: razem / m.dni,
      }
    }),
    zOszczednosciZl: zOszczednosci,
    depozytZl: depozyt,
    wykorzystanyZl: wykorzystany,
    zwrotNadplatyZl: zwrot,
    przepadaZl: depozyt - zeSprzedazy,
    zeSprzedazyZl: zeSprzedazy,
    razemZl: zOszczednosci + zeSprzedazy,
    kosztBezPvZl: (ro.zuzycieKwh || 0) * r.cenaZakupuZlKwh * zakup,
    cenaZakupuZlKwh: r.cenaZakupuZlKwh * zakup,
    wartoscOddanejZlKwh: wartoscOddanej,
  }
}
